import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import {
  ExpandedItem,
  ExpandedRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { BankAccount } from '@model/BankAccount'

import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { BankBadgeStatus } from './BankBadgeStatus'

type BankAccountsTableProps = {
  bankAccounts?: BankAccount[]
}

export const BankAccountsTable: React.FC<BankAccountsTableProps> = ({ bankAccounts = [] }) => {
  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()

  const [expandedRow, setExpandedRow] = useState<string>('')

  const toggleRow = (id: string) => {
    const next = expandedRow === id ? '' : id
    if (next) scrollIntoView([`bank-account-${id}`])
    setExpandedRow(next)
  }

  return (
    <Table noResults={!bankAccounts.length}>
      <TableHeader>
        <TableRow>
          <TableHead align='left'>{t('Beneficiary Name')}</TableHead>
          <TableHead align='left'>{t('IBAN/Account Number')}</TableHead>
          <TableHead align='left'>{t('Bank Name')}</TableHead>
          <TableHead align='center'>{t('Status')}</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {bankAccounts.map((bank) => {
          const isSelected = expandedRow === bank.id

          return (
            <React.Fragment key={bank.id}>
              <TableRow id={`bank-account-${bank.id}`}>
                <TableCell
                  onExpand={() => toggleRow(bank.id)}
                  expanded={isSelected}
                  align='left'
                  width={320}
                >
                  <SensitiveData>{bank.accountName}</SensitiveData>
                </TableCell>

                <TableCell width={280}>
                  <SensitiveData>{bank.bankAccount}</SensitiveData>
                </TableCell>

                <TableCell width={260}>
                  <SensitiveData>{bank.bankName}</SensitiveData>
                </TableCell>

                <TableCell align='center' width={180}>
                  <BankBadgeStatus {...bank.status} />
                </TableCell>
              </TableRow>

              <ExpandedRow expanded={isSelected} colSpan={4}>
                <ExpandedItem
                  label={t('Bank Address')}
                  value={bank.bankAddress}
                  condition={!!bank.bankAddress}
                  canCopy
                />
                <ExpandedItem
                  label={t('Swift')}
                  sensitive
                  value={bank.bankCode}
                  condition={!!bank.bankCode}
                />
                <ExpandedItem
                  label={t('Sort Code')}
                  sensitive
                  value={bank.bankCodeSort}
                  condition={!!bank.bankCodeSort}
                />
                <ExpandedItem
                  label={t('Documents')}
                  sensitive
                  value={bank.document?.name}
                  condition={!!bank.document?.name}
                />
              </ExpandedRow>
            </React.Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
